import dayjs from "dayjs";
import "dayjs/locale/en";
import utc from "dayjs/plugin/utc";
import type { BadgeColor } from "@corp-ais/omniark-ui";
import { DISPLAY_DATE_TIME_UTC_OFFSET_HOURS } from "@shared/constants/date-format.constants";

dayjs.extend(utc);

/** Where today falls relative to a price's ValidFor (see CONTEXT.md). */
export type ValidForState = "upcoming" | "active" | "endingSoon" | "expired";

/** Display model for a price's ValidFor (`prices[i].validFor`). */
export interface ValidForView {
  /** Compact range for the tier card / identity chip, in Thailand time. */
  label: string;
  /** Tooltip text: both bounds with their clock and the zone spelled out. */
  fullLabel: string;
  state: ValidForState;
  /** Badge text — present only for the states worth a warning; `active`
   *  deliberately carries none so a healthy card stays quiet. */
  stateLabel?: string;
  start: string | null;
  end: string | null;
  zone: string;
}

/** A price is "Ending soon" when its window closes within this many days. */
export const VALID_FOR_ENDING_SOON_DAYS = 30;

const STATE_LABEL: Record<ValidForState, string | undefined> = {
  upcoming: "Upcoming",
  active: undefined,
  endingSoon: "Ending soon",
  expired: "Expired",
};

export const VALID_FOR_BADGE_COLOR: Record<
  ValidForState,
  BadgeColor | undefined
> = {
  upcoming: "info",
  active: undefined,
  endingSoon: "warning",
  expired: "error",
};

const ZONE_LABEL = "ICT (UTC+07:00)";

/** Parse one `validFor` bound. Bounds carry an explicit zone from the BE; a
 *  zone-less value is read as Thailand time rather than the browser's, so the
 *  window means the same instant wherever the author opens the page. */
function parseBound(value: string | null | undefined): dayjs.Dayjs | undefined {
  const raw = value?.trim();
  if (!raw) return undefined;
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const parsed = zoned
    ? dayjs(raw)
    : dayjs(`${raw.length === 10 ? `${raw}T00:00:00` : raw}+07:00`);
  // English month names to match the rest of this form: shared date dayjs setup
  // sets the app-wide dayjs locale to Thai, which would otherwise leak in here.
  return parsed.isValid()
    ? parsed.utcOffset(DISPLAY_DATE_TIME_UTC_OFFSET_HOURS).locale("en")
    : undefined;
}

const isMidnight = (d: dayjs.Dayjs) =>
  d.hour() === 0 && d.minute() === 0 && d.second() === 0;

/** Start hides a 00:00 clock (the usual "from the start of that day"). End
 *  always shows its clock: an end at 00:00 closes at the START of that day, so
 *  "31 May 2036" alone would read as one day longer than the catalog means,
 *  and any other clock is real information. */
const fmtStart = (d: dayjs.Dayjs) =>
  d.format(isMidnight(d) ? "D MMM YYYY" : "D MMM YYYY HH:mm");
const fmtEnd = (d: dayjs.Dayjs) => d.format("D MMM YYYY HH:mm");
const fmtFull = (d: dayjs.Dayjs) => d.format("D MMM YYYY HH:mm");

/**
 * Describe a price's ValidFor for display, or `undefined` when the price
 * carries no usable bound. `now` is injected so callers (and specs) control
 * the reference instant; state is computed on instants, so the zone only
 * affects how the dates read, never which state applies.
 */
export function describeValidFor(
  validFor:
    | { startDateTime?: string | null; endDateTime?: string | null }
    | null
    | undefined,
  now: Date,
): ValidForView | undefined {
  const start = parseBound(validFor?.startDateTime);
  const end = parseBound(validFor?.endDateTime);
  if (!start && !end) return undefined;

  let label: string;
  let fullLabel: string;
  if (start && end) {
    label = `${fmtStart(start)} – ${fmtEnd(end)}`;
    fullLabel = `${fmtFull(start)} – ${fmtFull(end)} ${ZONE_LABEL}`;
  } else if (start) {
    label = `From ${fmtStart(start)}`;
    fullLabel = `From ${fmtFull(start)} ${ZONE_LABEL}, no end date`;
  } else {
    label = `Until ${fmtEnd(end!)}`;
    fullLabel = `Until ${fmtFull(end!)} ${ZONE_LABEL}, no start date`;
  }

  const t = now.getTime();
  let state: ValidForState = "active";
  if (end && t >= end.valueOf()) state = "expired";
  else if (start && t < start.valueOf()) state = "upcoming";
  else if (
    end &&
    end.valueOf() - t <= VALID_FOR_ENDING_SOON_DAYS * 24 * 60 * 60 * 1000
  )
    state = "endingSoon";

  return {
    label,
    fullLabel,
    state,
    stateLabel: STATE_LABEL[state],
    start: start ? fmtFull(start) : null,
    end: end ? fmtEnd(end) : null,
    zone: ZONE_LABEL,
  };
}
